import { addBreadcrumb, captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import { useAlertBoxCalculatorAccessMutation } from "web_myplann/Api/Portfolio/useAlertBoxCalculatorAccessMutation";
import { useRecordAlertBoxActionMutation } from "web_myplann/Api/Portfolio/useRecordAlertBoxActionMutation";
import {
	type AlertBoxActionEnum,
	AlertBoxActionStateEnum,
	NsureCalculatorTypeEnum,
	type PortfolioAlertBoxCalculatorAccessResponseDTO,
	type PortfolioAlertBoxDTO,
	type PortfolioEventTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { ApiCallError } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	getTranslation,
	Resources,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCalculatorPrefillSnackbar } from "web_myplann/NotificationCenter/EventAlert/calculatorPrefillSnackbar";
import { supportedNavigationTypesState } from "web_myplann/Shared/State/appInfoAtom";
import { isNavigationSupported } from "web_myplann/Shared/Utils/isNavigationSupported";
import { device, NavigationTypes } from "web_myplann/Utils/device";
import type { NsureCalculatorType } from "web_myplann/Utils/WebViewModel";

export type PendingAlertAction = {
	eventType: PortfolioEventTypeEnum;
	eventDate: string;
	action: AlertBoxActionEnum;
};

type OpenCalculator = (
	action: AlertBoxActionEnum,
	alert: Pick<PortfolioAlertBoxDTO, "eventType" | "eventDate">,
) => void;

/**
 * Which native calculator screen opens for the calculator the backend resolved
 * from the contract's product category and the action. The backend is the
 * single source of truth; this is a 1:1 rename to the shell's identifiers.
 */
const nativeCalculatorByType: Record<
	NsureCalculatorTypeEnum,
	NsureCalculatorType
> = {
	[NsureCalculatorTypeEnum.CarMoto]: "carMotoInsurance",
	[NsureCalculatorTypeEnum.Property]: "propertyInsurance",
	[NsureCalculatorTypeEnum.Travel]: "travelInsurance",
};

/**
 * Opens the nsure calculator in the native shell for an insurance CTA
 * (renew / recalculate / sign new travel), prefilled from the contract when
 * possible, and then records the "Opened" state ("Naposledy otevřeno"
 * subtext). Shells without the calculator screen only record "Opened".
 *
 * Only one calculator request runs at a time; `pendingAction` identifies the
 * alert + action whose button shows the loading state.
 */
export const useOpenNsureCalculator = (
	contractId: number,
): {
	openCalculator: OpenCalculator;
	pendingAction: PendingAlertAction | null;
} => {
	const { mutate: recordAction } = useRecordAlertBoxActionMutation();
	const { mutateAsync: requestCalculatorAccess } =
		useAlertBoxCalculatorAccessMutation();
	const queryClient = useQueryClient();
	const supportedNavigationTypes = useAtomValue(supportedNavigationTypesState);
	const [pendingAction, setPendingAction] = useState<PendingAlertAction | null>(
		null,
	);
	// Synchronous re-entrancy lock: a double tap must not fire a second
	// request before the pending state has re-rendered the buttons.
	const calculatorPendingRef = useRef(false);
	// The access request cannot be aborted, so after it resolves we only check
	// whether the component is still mounted before navigating. Set in the
	// effect (not only in the initializer) to stay correct under StrictMode's
	// mount/unmount/mount cycle.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const openCalculator = useCallback<OpenCalculator>(
		(action, { eventType, eventDate }) => {
			const recordOpened = () =>
				recordAction({
					contractId,
					eventType,
					eventDate,
					action,
					state: AlertBoxActionStateEnum.Opened,
				});

			if (
				!isNavigationSupported(
					supportedNavigationTypes,
					NavigationTypes.NsureCalculatorScreen,
				)
			) {
				addBreadcrumb({
					category: "event-alert",
					level: "info",
					message:
						"Native shell does not support NsureCalculatorScreen; recording Opened only",
					data: { action },
				});
				recordOpened();
				return;
			}

			if (calculatorPendingRef.current) {
				return;
			}
			calculatorPendingRef.current = true;
			setPendingAction({ eventType, eventDate, action });

			const run = async () => {
				let access: PortfolioAlertBoxCalculatorAccessResponseDTO;
				try {
					access = await requestCalculatorAccess({
						contractId,
						eventType,
						eventDate,
						action,
					});
				} catch (error) {
					// Toast + Sentry are handled by the global MutationCache onError.
					if (error instanceof ApiCallError && error.data.status === 400) {
						// The event or action is no longer offered; refresh the box.
						void queryClient.invalidateQueries({
							queryKey: PortfolioKeys.alertBoxesAll(contractId),
						});
					}
					return;
				}
				if (!mountedRef.current) {
					return;
				}

				// Guards against a backend that predates `calculatorType` or adds a
				// value this build does not know.
				const nsureCalculatorType: NsureCalculatorType | undefined =
					nativeCalculatorByType[access.calculatorType];
				if (!nsureCalculatorType) {
					captureException(
						new Error(
							`Unknown nsure calculator type ${String(access.calculatorType)}`,
						),
						{ extra: { action, calculatorType: access.calculatorType } },
					);
					toast.error(getTranslation(Resources.Common.somethingWentWrong));
					return;
				}

				try {
					const { payload } = await device.openNsureCalculator(
						access.url,
						nsureCalculatorType,
					);
					if (!payload.navigated) {
						toast.error(getTranslation(Resources.Common.somethingWentWrong));
						return;
					}
				} catch (error) {
					captureException(error);
					toast.error(getTranslation(Resources.Common.somethingWentWrong));
					return;
				}

				const snackbar = getCalculatorPrefillSnackbar(access.prefillResult);
				if (snackbar) {
					device
						.showSnackbar(getTranslation(snackbar.textKey), snackbar.variant)
						.catch(() => undefined);
				}
				recordOpened();
			};
			void run().finally(() => {
				calculatorPendingRef.current = false;
				if (mountedRef.current) {
					setPendingAction(null);
				}
			});
		},
		[
			contractId,
			queryClient,
			recordAction,
			requestCalculatorAccess,
			supportedNavigationTypes,
		],
	);

	return { openCalculator, pendingAction };
};
